/* eslint-disable react/destructuring-assignment */
import { FormApi } from 'final-form';
import { equals, isEmpty } from 'ramda';
import React, { Component } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { RouteComponentProps, StaticContext } from 'react-router';

import { LoadingOverlay } from '../../components/LoadingOverlay';
import {
  CreateForecastComponent,
  CreateForecastMutationFn,
  DeleteForecastFromProductionComponent,
  DeleteForecastFromProductionMutationFn,
  Forecast,
  GetForecastComponent,
  GetQueuesComponent,
  PutForecastToProductionComponent,
  PutForecastToProductionMutationFn,
  Queue,
  StateEnum,
  UpdateForecastComponent,
  UpdateForecastMutationFn,
  UpdateForecastNameComponent,
  UpdateForecastNameMutationFn,
} from '../../graphql/components';
import { PATH_FORECASTING_STUDIO, PATH_FORECASTING_STUDIO_EDIT } from '../../router/routes';
import '../../styles/forecasting.scss';
import { ActionRouteType, CreateEditView, CreateEditViewType, IdRouteType } from '../../types';
import { getQueue } from '../../utils/selectors';
import { Blank } from '../Blank/Blank';
import { NotFound } from '../NotFound/NotFound';
import { FormCreateForecast } from './ForecastBaseInfo/FormCreateForecast';
import { isSelectedQueueChatType } from './ForecastBaseInfo/FormForecastBase';
import { FormConfigureForecastContainer } from './ForecastingComposer/FormConfigureForecastContainer';
import {
  Adjustment,
  CreateForecastState,
  defaultCreateForecastProps,
  defaultForecastInfoVariables,
  defaultUpdateForecastVariables,
} from './config';
import { SKIP_SWITCH_TO_EDIT } from './constants';
import {
  FormSpyValues,
  FormValues,
  composeForecastInfo,
  getFirstStepValues,
  getFormInitialValues,
  getInitialState,
} from './utils/values';
import { validateCreateForecastForm } from './validateCreateForecastForm';

type CreateForecastContainerProps = RouteComponentProps<
  ActionRouteType & IdRouteType,
  StaticContext,
  { forecast?: Forecast; queueTimezone?: string }
>;

interface CreateForecastProps {
  action: CreateEditViewType;
  createForecast: CreateForecastMutationFn;
  deleteForecastFromProduction: DeleteForecastFromProductionMutationFn;
  history: CreateForecastContainerProps['history'];
  initialForecast?: Forecast;
  putForecastToProduction: PutForecastToProductionMutationFn;
  queues: Queue[];
  updateForecast: UpdateForecastMutationFn;
  updateForecastName: UpdateForecastNameMutationFn;
}

export class CreateForecast extends Component<CreateForecastProps, CreateForecastState> {
  private formInitialValues?: FormValues;

  constructor(props: CreateForecastProps) {
    super(props);

    this.state = getInitialState(props.action, props.queues, props.initialForecast);
    this.formInitialValues = getFormInitialValues(this.state, props.initialForecast);

    this.createForecastAndPutToProduction = this.createForecastAndPutToProduction.bind(this);
    this.firstStepFormOnChange = this.firstStepFormOnChange.bind(this);
    this.initializeAdjustments = this.initializeAdjustments.bind(this);
    this.isDirty = this.isDirty.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.putToProduction = this.putToProduction.bind(this);
    this.removeFromProduction = this.removeFromProduction.bind(this);
    this.renderForm = this.renderForm.bind(this);
    this.updateAdjustments = this.updateAdjustments.bind(this);
    this.changeChartsTimezone = this.changeChartsTimezone.bind(this);
  }

  public renderForm(args: FormRenderProps<FormValues>) {
    const { isEdit, isView, isProduction, parametersChanged, chartsTimezone, queueTimezone, step } = this.state;

    return step === 0 ? (
      <FormCreateForecast
        {...args}
        action={this.props.action}
        adjustments={this.state.adjustments}
        updateAdjustments={this.updateAdjustments}
        isEdit={isEdit}
        isProduction={isProduction}
        isView={isView}
        onCancel={this.onCancel}
        onChange={this.firstStepFormOnChange}
        onNextStep={this.goToSecondStep}
        queues={this.props.queues}
        queueTimezone={queueTimezone!}
        shouldBlockLeave={this.isDirty()}
      />
    ) : (
      <FormConfigureForecastContainer
        {...args}
        action={this.props.action}
        adjustments={this.state.adjustments}
        createForecastAndPutToProduction={this.createForecastAndPutToProduction}
        forecastCreatedUpdated={this.state.forecastCreatedUpdated}
        initializeAdjustments={this.initializeAdjustments}
        isEdit={isEdit}
        isProduction={isProduction}
        isView={isView}
        onCancel={this.onCancel}
        onPreviousStep={this.goToFirstStep}
        parameters={this.state.parameters}
        parametersChanged={parametersChanged}
        putToProduction={this.putToProduction}
        isChatQueueType={isSelectedQueueChatType(this.props.queues, args.values.queueId)}
        removeFromProduction={this.removeFromProduction}
        shouldBlockLeave={this.isDirty()}
        updateAdjustments={this.updateAdjustments}
        chartsTimezone={chartsTimezone!}
        changeChartsTimezone={this.changeChartsTimezone}
        queueTimezone={queueTimezone!}
      />
    );
  }

  public render() {
    const { loading } = this.state;

    return (
      <LoadingOverlay loading={loading}>
        <Form<FormValues>
          onSubmit={this.onSubmit}
          render={this.renderForm}
          validate={validateCreateForecastForm}
          initialValues={this.formInitialValues}
          keepDirtyOnReinitialize
          initialValuesEqual={equals}
        />
      </LoadingOverlay>
    );
  }

  // eslint-disable-next-line react/sort-comp
  public initializeAdjustments(adjustments: Adjustment[] = []) {
    this.setState({ adjustments, adjustmentsUpdated: false });
  }

  public updateAdjustments(adjustments: Adjustment[]) {
    this.setState({ adjustments, adjustmentsUpdated: true, parametersChanged: true });
  }

  public changeChartsTimezone(timezone: string) {
    this.setState({ chartsTimezone: timezone });
  }

  protected onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
    const {
      adjustments,
      adjustmentsUpdated,
      forecastId,
      isCreate,
      isEdit,
      firstStepFormChanged,
      forecastCreatedUpdated,
      parameters,
      parametersChanged,
      step,
      queueTimezone,
    } = this.state;

    const { createForecast, updateForecast, updateForecastName, queues } = this.props;

    // Need to "un-dirty" the state to avoid redirect blocking
    this.setState({
      adjustmentsUpdated: false,
      loading: true,
      firstStepFormChanged: false,
      forecastCreatedUpdated: true,
      parametersChanged: false,
    });

    try {
      if (isCreate) {
        let forecastInfo;

        if (step === 0) {
          forecastInfo = composeForecastInfo({
            ...defaultForecastInfoVariables,
            ...getFirstStepValues(values, queueTimezone!),
          });
        } else {
          forecastInfo = composeForecastInfo(parameters);
        }

        const result = await createForecast({
          variables: {
            forecast: {
              adjustments,
              info: forecastInfo,
            },
          },
        });

        const forecastToStore = {
          ...forecastInfo,
          id: result.data?.createForecast?.id ?? '',
          state: StateEnum.NonProduction,
          creationDate: new Date().toISOString(),
        };

        if (!values[SKIP_SWITCH_TO_EDIT]) {
          form.change('id', forecastToStore.id); // Needed on next step to load newly created forecast data
          this.setState(
            {
              ...getInitialState(CreateEditView.EDIT, queues, forecastToStore, step),
              adjustments,
              forecastCreatedUpdated: true,
            },
            () =>
              this.props.history.replace(`${PATH_FORECASTING_STUDIO_EDIT}/${forecastToStore.id}`, {
                forecast: forecastToStore as Forecast,
              })
          );
        }
      }

      if (isEdit && forecastId) {
        if (!adjustmentsUpdated) {
          await updateForecastName({
            variables: {
              id: forecastId,
              name: values.name,
            },
          });
        } else {
          await updateForecast({
            variables: {
              id: forecastId,
              forecast: {
                adjustments,
                info: composeForecastInfo({
                  ...parameters,
                  name: values.name || parameters.name,
                }),
              },
            },
          });
        }
      }

      // Re-initialize form to have proper dirty state on subsequent form changes
      form.initialize(values);
    } catch (error) {
      this.setState({ adjustmentsUpdated, firstStepFormChanged, forecastCreatedUpdated, parametersChanged });
      form.change(SKIP_SWITCH_TO_EDIT, undefined);
      throw error;
    } finally {
      this.setState({ loading: false });
    }
  };

  protected onCancel() {
    this.props.history.push(PATH_FORECASTING_STUDIO);
  }

  protected async createForecastAndPutToProduction(form: FormApi<FormValues>, values: FormValues) {
    const { createForecast, history, putForecastToProduction, queues } = this.props;
    const { adjustments, parameters, step } = this.state;

    const forecastInfo = composeForecastInfo(parameters);

    try {
      const createResult = await createForecast({
        variables: {
          forecast: {
            adjustments,
            info: forecastInfo,
          },
        },
      });

      if (createResult) {
        const id = createResult.data?.createForecast?.id!;

        await putForecastToProduction({
          variables: { id },
        });

        const forecastToStore = {
          ...forecastInfo,
          id,
          creationDate: new Date().toISOString(),
          state: StateEnum.Production,
        };

        this.setState(
          {
            ...getInitialState(CreateEditView.EDIT, queues, forecastToStore, step),
            adjustments,
            forecastCreatedUpdated: true,
            isProduction: true,
          },
          () => {
            form.initialize({ ...values, id, state: StateEnum.Production });
            history.replace(`${PATH_FORECASTING_STUDIO_EDIT}/${id}`, { forecast: forecastToStore as Forecast });
          }
        );
      }
    } catch (e) {
      // `customFetch()` will display error message
    }
  }

  protected async putToProduction(callback: () => void) {
    const id = this.state.forecastId;

    if (id) {
      try {
        await callback();
        await this.props.putForecastToProduction({
          variables: { id },
        });
        this.setState({ isProduction: true });

        const { state = {} } = this.props.history.location;

        if (!isEmpty(state)) {
          this.props.history.replace({
            state: { ...state, forecast: { ...state.forecast, state: StateEnum.Production } as Forecast },
          });
        }
      } catch (e) {
        // `customFetch()` will display error message
      }
    }
  }

  protected async removeFromProduction() {
    const id = this.state.forecastId;

    if (id) {
      try {
        await this.props.deleteForecastFromProduction({ variables: { id } });
        this.setState({ isProduction: false });

        const { state = {} } = this.props.history.location;

        if (!isEmpty(state)) {
          this.props.history.replace({
            state: { ...state, forecast: { ...state.forecast, state: StateEnum.NonProduction } as Forecast },
          });
        }
      } catch (e) {
        // `customFetch()` will display error message
      }
    }
  }

  private goToFirstStep = () => {
    this.setState({
      step: 0,
    });
  };

  private goToSecondStep = (values: FormValues) => {
    const { queues } = this.props;
    const { queueTimezone, parameters } = this.state;

    const createForecastParameters = getFirstStepValues(values, queueTimezone!);

    this.setState((state) => ({
      step: 1,
      chartsTimezone:
        values.queueId !== parameters.queueId ? getQueue(queues, values.queueId)?.timezone : state.chartsTimezone, // Keep selected charts timezone until queue is changed
      parameters: {
        ...state.parameters,
        name: createForecastParameters.name,
        ...(state.isCreate ? createForecastParameters : {}),
      },
    }));
  };

  private firstStepFormOnChange(props: FormSpyValues) {
    const { queues } = this.props;
    const { queueId } = props.values;

    this.setState(({ isCreate, isEdit }) => ({
      firstStepFormChanged: (isCreate && props.valid) || (isEdit && props.dirty),
      queueTimezone: queueId ? getQueue(queues, queueId)?.timezone : undefined,
    }));
  }

  private isDirty() {
    return this.state.parametersChanged || this.state.firstStepFormChanged;
  }
}

export const CreateForecastContainer: React.FC<CreateForecastContainerProps> = ({ history, match }) => {
  const { action, id } = match.params;
  const { state: { forecast: forecastInfoFromState, queueTimezone } = {} } = history.location;
  const isCreate = action === CreateEditView.CREATE;

  const shouldGetForecast = !isCreate && (!forecastInfoFromState || forecastInfoFromState.timezone !== queueTimezone);

  return (
    <GetQueuesComponent fetchPolicy="network-only">
      {(queues) => (
        <GetForecastComponent variables={{ id }} skip={!shouldGetForecast} fetchPolicy="network-only">
          {(forecast) => {
            const loading = queues.loading || forecast.loading;
            const error = queues.error || forecast.error;

            return (
              <LoadingOverlay loading={loading}>
                {(() => {
                  if (loading || error) {
                    return <Blank />;
                  }

                  const forecastInfo = forecast.data?.forecast?.info;

                  return shouldGetForecast && !forecastInfo ? (
                    <NotFound />
                  ) : (
                    <CreateForecastComponent variables={defaultCreateForecastProps}>
                      {(createForecast: CreateForecastMutationFn, { loading: createForecastLoading }) => (
                        <UpdateForecastComponent variables={defaultUpdateForecastVariables}>
                          {(updateForecast: UpdateForecastMutationFn, { loading: updateForecastLoading }) => (
                            <UpdateForecastNameComponent>
                              {(
                                updateForecastName: UpdateForecastNameMutationFn,
                                { loading: updateForecastNameLoading }
                              ) => (
                                <PutForecastToProductionComponent>
                                  {(
                                    putForecastToProduction: PutForecastToProductionMutationFn,
                                    { loading: putForecastToProductionLoading }
                                  ) => (
                                    <DeleteForecastFromProductionComponent>
                                      {(
                                        deleteForecastFromProduction: DeleteForecastFromProductionMutationFn,
                                        { loading: deleteForecastFromProductionLoading }
                                      ) => (
                                        <LoadingOverlay
                                          loading={
                                            createForecastLoading ||
                                            updateForecastLoading ||
                                            updateForecastNameLoading ||
                                            putForecastToProductionLoading ||
                                            deleteForecastFromProductionLoading
                                          }
                                        >
                                          <CreateForecast
                                            action={action}
                                            createForecast={createForecast}
                                            initialForecast={shouldGetForecast ? forecastInfo : forecastInfoFromState}
                                            history={history}
                                            updateForecast={updateForecast}
                                            updateForecastName={updateForecastName}
                                            putForecastToProduction={putForecastToProduction}
                                            deleteForecastFromProduction={deleteForecastFromProduction}
                                            queues={queues.data?.queues || []}
                                          />
                                        </LoadingOverlay>
                                      )}
                                    </DeleteForecastFromProductionComponent>
                                  )}
                                </PutForecastToProductionComponent>
                              )}
                            </UpdateForecastNameComponent>
                          )}
                        </UpdateForecastComponent>
                      )}
                    </CreateForecastComponent>
                  );
                })()}
              </LoadingOverlay>
            );
          }}
        </GetForecastComponent>
      )}
    </GetQueuesComponent>
  );
};
